import { createTheme } from '@mui/material/styles';

// Color palette for minimal fitness app design
const palette = {
  primary: {
    main: '#4CAF50', // Clean green
    light: '#81C784',
    dark: '#388E3C',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#00BCD4', // Teal accent
    light: '#4DD0E1',
    dark: '#0097A7',
    contrastText: '#ffffff',
  },
  background: {
    default: '#F8F9FA', // Light gray background
    paper: '#FFFFFF',
  },
  text: {
    primary: '#2E3440', // Dark gray for good contrast
    secondary: '#5E6C7F',
    disabled: '#9CA3AF',
  },
  grey: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
  },
  warning: {
    main: '#F59E0B',
    light: '#FCD34D',
    dark: '#D97706',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
  },
  info: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#2563EB',
  },
};

// Typography configuration for clean, readable text
const typography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  h1: {
    fontSize: '2.125rem',
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: '-0.025em',
  },
  h2: {
    fontSize: '1.875rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.025em',
  },
  h3: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none', // Remove uppercase transformation
    letterSpacing: '0.02em',
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.4,
    letterSpacing: '0.03em',
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
};

// Spacing and shape configuration
const spacing = 8; // 8px base unit

const shape = {
  borderRadius: 8, // Subtle rounded corners
};

// Shadow system for subtle elevation
const shadows = [
  'none',
  '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
];

// Create the theme
const theme = createTheme({
  palette,
  typography,
  spacing,
  shape,
  shadows,
  components: {
    // Button component overrides
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontWeight: 500,
          fontSize: '0.875rem',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    // Card component overrides
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #F3F4F6',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    // TextField component overrides
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: '#E5E7EB',
            },
            '&:hover fieldset': {
              borderColor: '#D1D5DB',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#4CAF50',
              borderWidth: 2,
            },
          },
        },
      },
    },
    // AppBar component overrides
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#2E3440',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          borderBottom: '1px solid #F3F4F6',
        },
      },
    },
    // Paper component overrides
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
    // Chip component overrides
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
});

export default theme;